import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProductSettings } from '@workspace/utils-react';
import type { Settings } from '../../AppContainer';
import { checkBatchAdminAccess } from '../../api/batch-admin';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { Alert, AlertTitle, AlertDescription } from '../../components/ui/alert';
import { Spinner } from '../../components/ui/spinner';
import { ShieldX, Home } from 'lucide-react';
import { BatchProcessContent } from './BatchProcessContent';
import { ProjectSettingsContent } from './ProjectSettingsContent';
import { PromptsContent } from './PromptsContent';
import { MembersManagementContent } from './MembersManagementContent';

export function SettingsArea() {
  const { apiUrl } = useProductSettings<Settings>();
  const navigate = useNavigate();
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [accessLoading, setAccessLoading] = useState(true);

  useEffect(() => {
    async function checkAccess() {
      try {
        const result = await checkBatchAdminAccess(apiUrl);
        setHasAccess(result.has_access);
      } catch {
        setHasAccess(false);
      } finally {
        setAccessLoading(false);
      }
    }
    checkAccess();
  }, [apiUrl]);

  if (accessLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner className="size-8" />
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="container mx-auto p-6 max-w-2xl">
        <Alert variant="destructive">
          <ShieldX className="h-5 w-5" />
          <div>
            <AlertTitle>Access Denied</AlertTitle>
            <AlertDescription>
              You do not have permission to access Settings. Contact your administrator to request access.
            </AlertDescription>
          </div>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-[1400px] space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Settings</h1>
        <Button variant="outline" size="sm" onClick={() => navigate('/')}>
          <Home className="h-4 w-4 mr-2" />
          Home
        </Button>
      </div>

      <Tabs defaultValue="batch-process">
        <TabsList>
          <TabsTrigger value="batch-process">Batch Process</TabsTrigger>
          <TabsTrigger value="project-settings">Project Settings</TabsTrigger>
          <TabsTrigger value="prompts">Prompts</TabsTrigger>
          <TabsTrigger value="members">Members</TabsTrigger>
        </TabsList>

        <TabsContent value="batch-process">
          <BatchProcessContent />
        </TabsContent>

        <TabsContent value="project-settings">
          <ProjectSettingsContent />
        </TabsContent>

        <TabsContent value="prompts">
          <PromptsContent />
        </TabsContent>

        <TabsContent value="members">
          <MembersManagementContent />
        </TabsContent>
      </Tabs>
    </div>
  );
}
